import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, PenTool, Flame, Footprints, Sparkles, CalendarDays, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { Motif } from "../components/Motifs";
import {
  PMU_STYLES,
  PMU_BROW_PRICES,
  PMU_EXTRA_PRICES,
  PMU_HEALING,
  PMU_AFTERCARE_DO,
  PMU_AFTERCARE_DONT,
  PMU_CONTRAINDICATIONS,
  LASER_NOTES,
} from "../data/pmu";

const STEPS = [
  { num: "01", icon: MessageCircle, title: "Konzultáció", text: "Időpontfoglalás esetén a kiválasztott tetoválóval egyeztetsz — ötlet, méret, elhelyezés, ár." },
  { num: "02", icon: PenTool, title: "Egyedi terv", text: "A művész elkészíti a személyre szabott mintát, amit a szúrás napján együtt véglegesítetek." },
  { num: "03", icon: Flame, title: "Alkotás", text: "Steril környezet, nyugodt légkör — és egy tetoválás, amit életed végéig viselsz." },
];

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      <PageHeader
        testId="pricing-header"
        motif="needle"
        eyebrow="Tetoválás árak"
        title={<>Átlátható <span className="italic text-[#C04B58]">árak.</span></>}
        description="Tűbontási alapár, nulla apróbetű. A végső ár a mintától, méretétől és elhelyezésétől függ — pontosat a tetoválóddal egyeztetve kapsz."
      />

      <section className="mx-auto max-w-7xl px-5 pb-28 sm:px-8">
        <div className="grid gap-6 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <div className="card-dark relative h-full overflow-hidden p-10 sm:p-12" data-testid="tattoo-price-card">
              <p className="eyebrow">Tűbontási alapár</p>
              <p className="font-display mt-6 text-6xl font-semibold text-white sm:text-7xl" data-testid="tattoo-hourly-price">
                20 000 Ft
                <span className="block text-2xl font-normal text-zinc-500 sm:text-3xl">tűbontási alapár</span>
              </p>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-zinc-400">
                Minden tetoválás egyedi árazással készül — az ár nagyban függ a kért mintától, nagyságától és
                helyétől. Ezt az adott tetováló művésszel tudod részletesebben átbeszélni.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/kapcsolat" data-testid="pricing-booking-btn" className="btn-primary">
                  Időpontkérés <ArrowRight size={16} />
                </Link>
                <Link to="/muveszek" data-testid="pricing-artists-btn" className="btn-ghost">
                  Válassz tetoválót
                </Link>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-2">
            <div className="card-dark relative h-full overflow-hidden border-[#43262C]/40 bg-gradient-to-b from-[#141414] to-[#160a0c] p-10 sm:p-12" data-testid="walkin-card">
              <div className="flex items-center gap-3">
                <Footprints size={18} className="text-[#9A3542]" />
                <p className="eyebrow">Walk-in</p>
              </div>
              <h3 className="font-display mt-6 text-3xl font-semibold leading-tight text-white">
                Csak <span className="italic text-[#C04B58]">besétálsz.</span>
              </h3>
              <p className="mt-5 text-sm leading-relaxed text-zinc-300" data-testid="walkin-text">
                „Walk-in szolgáltatás elérhető, ez annyit jelent, ha nem szeretnél időpontot foglalni, bejöhetsz a
                szalonba nyitvatartási időn belül, mivel minden nap van bent egy tetováló, aki tudja fogadni a
                besétálós vendégeket."
              </p>
              <p className="mt-6 font-mono2 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                1064 Budapest, Podmaniczky utca 75.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="mt-20">
          <Reveal>
            <p className="eyebrow">Így működik</p>
            <h2 className="font-display mt-4 text-3xl font-semibold text-white sm:text-4xl">Három lépés a bőrödön</h2>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1}>
                <div className="card-dark group h-full p-8" data-testid={`pricing-step-${s.num}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono2 text-sm tracking-[0.3em] text-[#9A3542]">{s.num} /</span>
                    <s.icon size={20} className="text-zinc-500 transition-colors group-hover:text-[#C04B58]" />
                  </div>
                  <h3 className="font-display mt-5 text-2xl font-semibold text-white">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal className="mt-16">
          <div className="card-dark flex flex-col items-start justify-between gap-6 p-8 sm:flex-row sm:items-center" data-testid="pricing-piercing-teaser">
            <div>
              <p className="eyebrow">Piercing</p>
              <p className="font-display mt-2 text-2xl font-semibold text-white">Piercing áraink 7 000 Ft-tól — tételes lista a piercing oldalon.</p>
            </div>
            <Link to="/piercing" data-testid="pricing-piercing-btn" className="btn-ghost shrink-0">
              Piercing árak <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* SMINKTETOVÁLÁS & LÉZERES ELTÁVOLÍTÁS */}
      <section id="sminktetovalas" className="relative overflow-hidden border-t border-[#202023] bg-[#0F0F11] py-24 sm:py-32" data-testid="pmu-section">
        <Motif type="flower" className="pointer-events-none absolute -left-8 top-20 w-44 select-none text-[#C04B58] opacity-[0.06]" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="flex items-center gap-3">
              <Sparkles size={18} className="text-[#9A3542]" />
              <p className="eyebrow">Sminktetoválás & eltávolítás</p>
            </div>
            <h2 className="font-display mt-4 text-3xl font-semibold text-white sm:text-4xl lg:text-5xl">
              Szemöldök, szemhéj, <span className="italic text-[#C04B58]">eltávolítás.</span>
            </h2>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-zinc-400">
              A sminktetoválásokat és a lézeres tetoválás eltávolítást Jaszper Andrea végzi — természetes, lágy
              stílusban, több neves technikával (Ombre, Browlift, Aquarelle, GoInk).
            </p>
          </Reveal>

          {/* STÍLUSOK */}
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="pmu-styles">
            {PMU_STYLES.map((s, i) => (
              <Reveal key={s.n} delay={(i % 3) * 0.08}>
                <article className="card-dark group h-full p-7" data-testid={`pmu-style-${s.n}`}>
                  <span className="font-mono2 text-sm tracking-[0.3em] text-[#9A3542]">{s.n} /</span>
                  <h3 className="font-display mt-4 text-xl font-semibold text-white transition-colors group-hover:text-[#C04B58]">
                    {s.name}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{s.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>

          {/* ÁRAK */}
          <div className="mt-16 grid gap-6 lg:grid-cols-2" data-testid="pmu-prices">
            <Reveal>
              <div className="card-dark h-full p-8">
                <p className="eyebrow">Szemöldöktetoválás</p>
                <div className="mt-5 divide-y divide-[#1e1e1e]">
                  {PMU_BROW_PRICES.map((name) => (
                    <div key={name} className="flex items-baseline gap-4 py-3.5" data-testid={`pmu-price-${name.toLowerCase().replace(/\s/g, "-")}`}>
                      <span className="font-display text-lg font-medium text-white">{name}</span>
                      <span className="price-leader mx-2 flex-1" />
                      <span className="font-mono2 text-[11px] uppercase tracking-[0.15em] text-zinc-500">érdeklődj a szalonban</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex h-full flex-col gap-6">
                <div className="card-dark p-8">
                  <p className="eyebrow">Szemhéj tetoválás</p>
                  <div className="mt-5 flex items-baseline gap-4" data-testid="pmu-price-szemhej">
                    <span className="font-display text-lg font-medium text-white">Szemhéj tetoválás</span>
                    <span className="price-leader mx-2 flex-1" />
                    <span className="font-mono2 text-[11px] uppercase tracking-[0.15em] text-zinc-500">érdeklődj a szalonban</span>
                  </div>
                </div>
                <div className="card-dark relative flex-1 border-[#43262C]/40 bg-gradient-to-b from-[#141414] to-[#160a0c] p-8" data-testid="laser-removal-card">
                  <p className="eyebrow">Lézeres tetoválás eltávolítás</p>
                  <p className="font-display mt-4 text-3xl font-semibold text-white sm:text-4xl" data-testid="laser-removal-price">
                    25 000 – 40 000 Ft
                    <span className="block text-lg font-normal text-zinc-500">/ alkalom</span>
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-zinc-400">
                    A pontos árat konzultáció után határozzuk meg — a tetoválás mérete, színe és kora alapján.
                  </p>
                  <Link to="/kapcsolat" data-testid="laser-removal-btn" className="btn-primary mt-6 !px-5 !py-2.5 !text-[11px]">
                    Konzultációt kérek <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>

          {/* LÉZERES ELTÁVOLÍTÁS — FONTOS TUDNIVALÓK */}
          <div className="mt-16" data-testid="laser-notes">
            <Reveal>
              <div className="card-dark border-[#43262C]/40 bg-gradient-to-b from-[#141414] to-[#160a0c] p-8 sm:p-10">
                <div className="flex items-center gap-3">
                  <AlertTriangle size={18} className="text-[#C04B58]" />
                  <p className="eyebrow">Lézeres tetováláseltávolítás</p>
                </div>
                <h3 className="font-display mt-4 text-2xl font-semibold text-white sm:text-3xl">
                  Fontos <span className="italic text-[#C04B58]">tudnivalók.</span>
                </h3>
                <ul className="mt-8 grid gap-x-10 gap-y-6 sm:grid-cols-2">
                  {LASER_NOTES.map((n) => (
                    <li key={n.title} className="flex gap-4" data-testid={`laser-note-${n.title.toLowerCase().replace(/\s/g, "-")}`}>
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C04B58]" />
                      <p className="text-sm leading-relaxed text-zinc-400">
                        <span className="font-medium text-white">{n.title}:</span> {n.text}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          {/* GYÓGYULÁSI FOLYAMAT */}
          <div className="mt-20" data-testid="pmu-healing">
            <Reveal>
              <div className="flex items-center gap-3">
                <CalendarDays size={18} className="text-[#9A3542]" />
                <p className="eyebrow">Gyógyulási folyamat</p>
              </div>
              <h3 className="font-display mt-4 text-2xl font-semibold text-white sm:text-3xl">
                Így gyógyul a szemöldöktatoválás — <span className="italic text-[#C04B58]">napról napra</span>
              </h3>
            </Reveal>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {PMU_HEALING.map((h, i) => (
                <Reveal key={h.day} delay={(i % 3) * 0.08}>
                  <div className="card-dark h-full p-6" data-testid={`healing-day-${i + 1}`}>
                    <span className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#C04B58]">{h.day}</span>
                    <p className="mt-3 text-sm leading-relaxed text-zinc-400">{h.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* UTÓÁPOLÁS */}
          <div className="mt-20 grid gap-6 lg:grid-cols-2" data-testid="pmu-aftercare">
            <Reveal>
              <div className="card-dark h-full p-8">
                <p className="eyebrow">Utóápolás — így csináld</p>
                <ul className="mt-6 space-y-4">
                  {PMU_AFTERCARE_DO.map((t, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-zinc-300">
                      <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-[#9A3542]" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="card-dark h-full p-8">
                <p className="eyebrow">Utóápolás — ezt kerüld</p>
                <ul className="mt-6 space-y-4">
                  {PMU_AFTERCARE_DONT.map((t, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-zinc-300">
                      <XCircle size={17} className="mt-0.5 shrink-0 text-[#C04B58]" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          {/* KIZÁRÓ OKOK */}
          <Reveal className="mt-20">
            <div className="card-dark relative overflow-hidden border-[#43262C]/40 p-8 sm:p-10" data-testid="pmu-contraindications">
              <div className="flex items-center gap-3">
                <AlertTriangle size={18} className="text-[#C04B58]" />
                <p className="eyebrow">Kizáró okok</p>
              </div>
              <h3 className="font-display mt-4 text-2xl font-semibold text-white sm:text-3xl">Mikor nem tudjuk vállalni a kezelést?</h3>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-zinc-400">
                A vendégeink biztonsága az első. Az alábbi esetek fennállásakor a sminktetoválást sajnos nem tudjuk
                elvégezni:
              </p>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {PMU_CONTRAINDICATIONS.map((c) => (
                  <span key={c} className="rounded-full border border-[#43262C] bg-[#9A3542]/8 px-4 py-1.5 text-xs text-zinc-300" data-testid={`contra-${c.toLowerCase().replace(/[\s,]/g, "-")}`}>
                    {c}
                  </span>
                ))}
              </div>
              <p className="mt-6 text-sm leading-relaxed text-zinc-400">
                Bizonyos esetekben orvosi hozzájárulással dolgozhatunk együtt — ha bizonytalan vagy, kérdezz minket
                bátran a konzultáció előtt.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
